// React
import { useEffect, useState } from "react";

// MUI
import { Box, Container, Grid, Stack, useMediaQuery } from "@mui/material";

// LAYOUT
import Layout from "src/components/core/Layout";

// REDUX
import {
  setStakeStage,
  setUnstakeStage,
  useStakingSliceActions,
  useStakingState,
} from "src/store/features/staking.slice";

// Components already created
import { BestPerformerHeader as StyledHeader } from "src/components/portfolio/balance/BalanceDefault";
import { YourCryptoPanel as StyledBorderContainer } from "src/components/portfolio/styles/portifolio.styles";
import { DcaContainer as StyledShadowBoxContainer } from "src/components/dca/styled-components/DcaContainer";
import { HOLDINGS_DATA_DEFAULT } from "src/components/portfolio/defaults";
import DonutChart from "src/components/portfolio/balance/DonutChart";
import { FilterSelection } from "src/components/dca/StrategiesTable";
import { TitleWrapper } from "./TradeV2";
import InfoIcon from "src/icons/portfolio/InfoIcon";
import Title from "src/components/common/Title";

// STYLED COMPONENTS
import {
  BorderLinearProgress,
  StyledBadge,
  StyledLargeChipbutton,
  StyledSelectbuyorsellspan,
  StyledStakedspan,
  StyledTextContent,
} from "src/components/staking/staking-styled-components";
import StyledButton from "src/components/dashboard/StyledButton";

// Page Components
import { useDispatch } from "react-redux";
import { mockStakingRewards } from "src/components/staking/mock";
import { SummaryAndHistoryTable } from "src/components/staking/SummaryAndHistoryTable";
import { TwoFAModal } from "src/components/modal/TwoFAModal";
import { StakeModal } from "src/components/staking/StakeModal";
import { UnstakeModal } from "src/components/staking/UnstakeModal";
import { ExportedSummarySnackbar } from "src/components/staking/ExportedSummarySnackbar";

const Staking = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [mobileTableSelected, setMobileTableSelected] = useState<"overview" | "stake">("overview");
  const { stakeModalOpen, unstakeModalOpen, twoFAModalOpen } = useStakingState();
  const { setTwoFAModalOpen } = useStakingSliceActions();
  const dispatch = useDispatch();
  const [rewardFilter, setRewardFilter] = useState<number>(0);

  // If selected filter is clear, set to 0
  useEffect(() => {
    if (rewardFilter === 3) setRewardFilter(0);
  }, [rewardFilter]);

  // use effect on modal close reset stages
  useEffect(() => {
    if (!stakeModalOpen) dispatch(setStakeStage("info"));
    if (!unstakeModalOpen) dispatch(setUnstakeStage("info"));
  }, [stakeModalOpen, unstakeModalOpen]);

  return (
    <Layout>
      {/* Mobile Header Switch */}
      <Container>
        <Grid container display={{ xs: "block", md: "none" }}>
          <StyledShadowBoxContainer style={{ padding: "12px 24px" }}>
            <Stack direction={"row"} gap={1} justifyContent={"center"}>
              <StyledLargeChipbutton
                selected={mobileTableSelected === "overview"}
                onClick={() => setMobileTableSelected("overview")}>
                Overview
              </StyledLargeChipbutton>
              <StyledLargeChipbutton
                selected={mobileTableSelected === "stake"}
                onClick={() => setMobileTableSelected("stake")}>
                Stake
              </StyledLargeChipbutton>
            </Stack>
          </StyledShadowBoxContainer>
        </Grid>
      </Container>

      {/* Desktop Title */}
      <Box display={{ xs: "none", md: "block" }} ml={3.5}>
        <TitleWrapper>
          <Title title={{ partA: "Stake with", partB: "Caleb & Brown" }} />
        </TitleWrapper>
      </Box>

      {/* Page Content */}
      <Container
        maxWidth="xl"
        sx={{
          display: (isMobile && mobileTableSelected === "overview") || !isMobile ? "block" : "none",
        }}>
        {/* Allocation and Rewards */}
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} md={6}>
            <StyledBorderContainer style={{ gap: "11px" }}>
              <StyledHeader>
                <span>Portfolio allocation</span>
                <InfoIcon
                  tooltip={
                    "This shows the percentage breakdown of all your actively staked assets across your staking validators. "
                  }
                  color={"#002035"}
                />
              </StyledHeader>
              <Grid container>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" justifyContent="flex-start">
                    {/* Donut Chart */}
                    <DonutChart data={HOLDINGS_DATA_DEFAULT} height="80px" width="80px" />

                    {/* Staking and Available */}
                    <Box display={"flex"} flexDirection={"column"} gap={1}>
                      <Stack direction={"row"} gap={1} alignItems={"center"} ml={2}>
                        <StyledBadge available={true} />
                        <StyledStakedspan>Staking: $xxxxxxxxxxxx</StyledStakedspan>
                      </Stack>
                      <Stack direction={"row"} gap={1} alignItems={"center"} ml={2}>
                        <StyledBadge available={false} />
                        <StyledStakedspan>Available: $xxxxxxxxxxxx</StyledStakedspan>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Portfolio allocation percentage */}
                  <Box mt={0.7}>
                    <StyledSelectbuyorsellspan>
                      35% of your portfolio is actively earning{" "}
                    </StyledSelectbuyorsellspan>
                  </Box>
                </Grid>
              </Grid>
            </StyledBorderContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledBorderContainer style={{ gap: "12px" }}>
              <StyledHeader style={{ height: "36px" }}>
                <StyledHeader>Top staking reward coins</StyledHeader>
                <InfoIcon tooltip={""} color={"#002035"} />
                <div style={{ marginLeft: "auto" }}>
                  <FilterSelection
                    filterState={rewardFilter}
                    setFilterState={setRewardFilter}
                    allStatuses={["Last 7 days", "Last 30 days", "All time", "Clear"]}
                  />
                </div>
              </StyledHeader>

              {mockStakingRewards.map((_, index) => {
                return (
                  <Grid key={index} container alignItems={"center"} height={"22.4px"}>
                    <Grid item xs={1} textAlign={"center"}>
                      X
                    </Grid>
                    <Grid item xs={5}>
                      <StyledStakedspan>Crypto Name</StyledStakedspan>
                    </Grid>
                    <Grid item xs={6} textAlign={"right"}>
                      <StyledStakedspan>$xxx xxx</StyledStakedspan>
                    </Grid>
                  </Grid>
                );
              })}
            </StyledBorderContainer>
          </Grid>
        </Grid>

        {/* Next Payout */}
        <Grid container spacing={2} mt={0.1}>
          <Grid item xs={12} md={4}>
            <StyledBorderContainer style={{ height: "122px" }}>
              <StyledHeader>
                <StyledHeader>Next Payout</StyledHeader>
                <InfoIcon
                  tooltip={
                    "Rewards are distributed weekly to all validators on Sunday, 11.59pm UTC. You earn staking rewards automatically while your assets remain staked, with no action required on your part."
                  }
                  color={"#002035"}
                />
              </StyledHeader>
              <StyledTextContent>2 Days</StyledTextContent>
              <BorderLinearProgress variant="determinate" value={90} />
            </StyledBorderContainer>
          </Grid>

          {/* Average APY */}
          <Grid item xs={12} md={4}>
            <StyledBorderContainer style={{ height: "122px" }}>
              <StyledHeader>
                <StyledHeader>Average APY</StyledHeader>
                <InfoIcon
                  tooltip={
                    "The annualised percentage yield (APY) representing your expected returns based on current reward rates. APY fluctuates based on network conditions and total validator size."
                  }
                  color={"#002035"}
                />
              </StyledHeader>
              <StyledTextContent>8.4%</StyledTextContent>
            </StyledBorderContainer>
          </Grid>

          {/* Lifetime Staking Rewards */}
          <Grid item xs={12} md={4}>
            <StyledBorderContainer style={{ height: "122px" }}>
              <StyledHeader>
                <StyledHeader>Lifetime Staking Rewards</StyledHeader>
                <InfoIcon
                  tooltip={
                    "The total value of all rewards you have earned from staking activities since account creation. This cumulative figure includes distributed rewards across all historical staking positions."
                  }
                  color={"#002035"}
                />
              </StyledHeader>
              <StyledTextContent>$1,000</StyledTextContent>
              {/* Portfolio allocation percentage */}
              <Box display={{ xs: "none", md: "none", lg: "flex" }}>
                <StyledSelectbuyorsellspan>
                  35% of your portfolio is actively earning{" "}
                </StyledSelectbuyorsellspan>
              </Box>
            </StyledBorderContainer>
          </Grid>
        </Grid>
      </Container>

      {/* This section is hidden on mobile user needs to select stake to view */}
      {((isMobile && mobileTableSelected === "stake") || !isMobile) && (
        <Container maxWidth="xl">
          <SummaryAndHistoryTable />
        </Container>
      )}

      {/* Stake Modal */}
      <StakeModal />

      {/* Unstake Modal */}
      <UnstakeModal />

      {/* 2FA Modal */}
      <TwoFAModal
        title="Verification"
        emailTemplate={"bankDetails2FA"}
        isOpen={twoFAModalOpen}
        handleClose={() => dispatch(setTwoFAModalOpen(false))}
        setError={() => {}}
        isLoading={false}>
        <Stack direction={"column"} gap={2} width={"100%"} textAlign={"left"} mt={4}>
          {/* On confirm clicked stage submitted */}
          <StyledButton
            onClick={() => {
              if (unstakeModalOpen) dispatch(setUnstakeStage("submitted"));
              else dispatch(setStakeStage("submitted"));
              dispatch(setTwoFAModalOpen(false));
            }}>
            Confirm
          </StyledButton>
        </Stack>
      </TwoFAModal>

      {/* Snack bar */}
      <ExportedSummarySnackbar />
    </Layout>
  );
};

export default Staking;
